import {
  CitationIsMobileContext,
  CitationSourcesContext,
  EMPTY_CITATION_SOURCES,
} from "@/contexts/citation-sources-context";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import {
  citationSourcesOf,
  researchOutputsOf,
  type CitationSource,
} from "@/lib/citation-sources";
import { useMemo, useRef, type ReactNode } from "react";

const isSameList = (
  a: ReadonlyArray<object>,
  b: ReadonlyArray<object>,
): boolean => a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Gives the answers of a thread the pages the conversation searched.
 *
 * The thread is re-read on every stream delta; the sources only change when a
 * search lands. A delta keeps every research output object (see
 * `researchOutputsOf`), so comparing them by identity is all a delta costs:
 * the value keeps its identity until a search lands, and no pill of the thread
 * re-renders before.
 *
 * It also tells the pills whether the thread is on a phone, read once for all
 * of them.
 */
export function CitationSourcesProvider({
  messages,
  children,
}: {
  messages: ReadonlyArray<{ readonly parts: ReadonlyArray<{ readonly type: string }> }>;
  children: ReactNode;
}) {
  const { isMobile } = useDeviceFormat();
  const previous = useRef<{
    outputs: ReadonlyArray<object>;
    sources: ReadonlyMap<string, CitationSource>;
  }>({ outputs: [], sources: EMPTY_CITATION_SOURCES });

  const sources = useMemo(() => {
    const outputs = researchOutputsOf(messages);
    if (isSameList(outputs, previous.current.outputs)) {
      return previous.current.sources;
    }
    const next = citationSourcesOf(outputs);
    previous.current = { outputs, sources: next };
    return next;
  }, [messages]);

  return (
    <CitationSourcesContext.Provider value={sources}>
      <CitationIsMobileContext.Provider value={isMobile}>
        {children}
      </CitationIsMobileContext.Provider>
    </CitationSourcesContext.Provider>
  );
}
